"use client";
import React, { useMemo } from "react";
import style from "../../drama/[dramaSlug]/Drama.module.css";
import { LanguageIcon } from "./PlayerIcons";

export interface AudioTrackOption {
  lang_code: string;
  lang_name: string;
  audio_file?: string; // Optional: builtin audio for default language
}

interface AudioLanguageSelectorProps {
  selectedLang: string;
  onSelect: (langCode: string) => void;
  isDubbed?: boolean;
  audioLanguages?: { lang_code: string; lang_name: string }[];
  audioTracks?: { lang_code: string; audio_file: string; }[];
}

// Lightweight dropdown control mirroring the "quality" and "caption" controls styling
export default function AudioLanguageSelector({ 
  selectedLang, 
  onSelect, 
  isDubbed = false,
  audioLanguages = [],
  audioTracks = []
}: AudioLanguageSelectorProps) {
  
  // Build the final language options based on isDubbed flag
  const finalLanguageOptions: AudioTrackOption[] = useMemo(() => {
    // Always start with English (built-in audio)
    const englishOption: AudioTrackOption = {
      lang_code: 'en',
      lang_name: 'English (Default)'
    };

    // If not dubbed, only show English
    if (!isDubbed) {
      return [englishOption];
    }

    // If dubbed, combine English with other available languages
    const dubbedOptions: AudioTrackOption[] = [];
    
    // Add other languages from audioLanguages (excluding English if present)
    audioLanguages.forEach(lang => {
      if (lang.lang_code !== 'en') {
        // Find corresponding audio file from audioTracks
        const audioTrack = audioTracks.find(track => track.lang_code === lang.lang_code);
        
        dubbedOptions.push({
          lang_code: lang.lang_code,
          lang_name: lang.lang_name,
          audio_file: audioTrack?.audio_file
        });
      }
    });

    return [englishOption, ...dubbedOptions];
  }, [isDubbed, audioLanguages, audioTracks]);

  // If not dubbed, show dropdown with only English option
  if (!isDubbed) {
    return (
      <div className={style.dropdownContainer} onClick={(e) => { e.stopPropagation(); }} onMouseDown={(e) => e.stopPropagation()}>
        <div className={style.logingroup}>
          <label className={`${style.loginlabel} ${style.commonBtn}`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <span className={style.captionbtn}>
              <LanguageIcon />
            </span>
          </label>
          <div className={`${style.dropdown} ${style.captiondropdown} ${style.langDropdown}`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            {finalLanguageOptions.map((opt) => (
              <div key={opt.lang_code} onClick={(e) => { e.stopPropagation(); onSelect(opt.lang_code); }} onMouseDown={(e) => e.stopPropagation()}>
                {selectedLang === opt.lang_code ? <>&#10003;</> : null} {opt.lang_name}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={style.dropdownContainer} onClick={(e) => { e.stopPropagation(); }} onMouseDown={(e) => e.stopPropagation()}>
      <div className={style.logingroup}>
        <label className={`${style.loginlabel} ${style.commonBtn}`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <span className={style.captionbtn}>
            <LanguageIcon />
          </span>
        </label>
        <div className={`${style.dropdown} ${style.captiondropdown} ${style.langDropdown}`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          {finalLanguageOptions.map((opt) => (
            <div key={opt.lang_code} onClick={(e) => { e.stopPropagation(); onSelect(opt.lang_code); }} onMouseDown={(e) => e.stopPropagation()}>
              {selectedLang === opt.lang_code ? <>&#10003;</> : null} {opt.lang_name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
